import type { ReactFlowNodeData } from '../../types/mindmap-components'
import { NODE_QUALITY_CONFIG } from './config'

function toWord(val: unknown): string {
  if (typeof val === 'string' || typeof val === 'number') return String(val)
  return ''
}

export interface QualityScoreResult {
  /** Overall quality score, 0–100 */
  score: number
  /** Whether this score meets the node type's AI generation threshold */
  meetsAIThreshold: boolean
  /** Per-field score breakdown (for potential tooltip detail) */
  fieldScores: Record<string, number>
}

function countWords(text: string): number {
  return text
    .trim()
    .split(/\s+/)
    .filter((w) => w.length > 0).length
}

/**
 * Calculate the data quality score for a node's data object.
 *
 * Returns score=0 / meetsAIThreshold=false for node types not in NODE_QUALITY_CONFIG.
 * Scoring is proportional per field: words / wordTarget * weight, capped at weight.
 */
export function calculateNodeQualityScore(data: ReactFlowNodeData): QualityScoreResult {
  const category = data.category as string
  const typeConfig = NODE_QUALITY_CONFIG[category]

  if (!typeConfig) {
    return { score: 0, meetsAIThreshold: false, fieldScores: {} }
  }

  const fieldScores: Record<string, number> = {}
  let totalScore = 0

  for (const fieldConfig of typeConfig.fields) {
    const raw = data[fieldConfig.field]
    let wordCount = 0

    if (fieldConfig.isArray && Array.isArray(raw)) {
      wordCount = (raw as unknown[]).reduce(
        (acc: number, item) => acc + countWords(toWord(item)),
        0
      )
    } else if (raw !== undefined && raw !== null && raw !== '') {
      wordCount = countWords(toWord(raw))
    }

    const ratio = Math.min(wordCount / fieldConfig.wordTarget, 1)
    const fieldScore = ratio * fieldConfig.weight
    fieldScores[fieldConfig.field] = fieldScore
    totalScore += fieldScore
  }

  const score = Math.round(totalScore)
  return {
    score,
    meetsAIThreshold: score >= typeConfig.aiThreshold,
    fieldScores,
  }
}
